"use client";

import { useEffect, useRef, useState } from "react";

const piliers = [
  {
    number: "01",
    page: "Page 04",
    title: "Standardisation",
    subtitle: "Une base commune pour chaque logement",
    color: "#6B7A5E",
    colorLight: "rgba(107,122,94,0.08)",
    colorBorder: "rgba(107,122,94,0.2)",
    description:
      "Sans standardisation, chaque logement est une île. Vos équipes improvisent, vos voyageurs vivent des expériences différentes, et vous passez votre temps à gérer des exceptions plutôt que des règles.",
    keyPoints: [
      "Process Book interne par logement",
      "Checklist d\'entrée et sortie standardisée",
      "Fiche logement complète (équipements, codes, spécificités)",
      "Protocole d'urgence documenté",
    ],
    terrain: {
      biens: 18,
      result:
        "Après 3 mois de standardisation sur 18 biens, cette conciergerie a réduit les appels de voyageurs de 62% et divisé par 3 les erreurs de ménage.",
    },
  },
  {
    number: "02",
    page: "Page 05",
    title: "Centralisation",
    subtitle: "Un point unique d\'accès à l\'information",
    color: "#C9A84C",
    colorLight: "rgba(201,168,76,0.08)",
    colorBorder: "rgba(201,168,76,0.2)",
    description:
      "Quand l'information est éparpillée entre WhatsApp, emails, notes papier et mémoire individuelle, chaque départ d'un collaborateur est une catastrophe. La centralisation crée la résilience.",
    keyPoints: [
      "Espace de travail partagé unique (type Notion ou équivalent)",
      "Accès par rôle : propriétaire, prestataire, équipe",
      "Historique des incidents par logement",
      "Calendrier partagé et suivi des interventions",
    ],
    terrain: {
      biens: 25,
      result:
        "Conciergerie 25 biens : en centralisant sur un outil unique, le temps de réponse aux urgences est passé de 47 min à 12 min en moyenne.",
    },
  },
  {
    number: "03",
    page: "Page 06",
    title: "Automatisation",
    subtitle: "Tout ce qui est répétitif doit être automatisé",
    color: "#7C6A5C",
    colorLight: "rgba(124,106,92,0.08)",
    colorBorder: "rgba(124,106,92,0.2)",
    description:
      "Un message de bienvenue J-3, un rappel check-out J+1, une demande d'avis J+2 — ces séquences sont prévisibles à 100%. Les automatiser libère du temps pour ce qui compte vraiment : l'humain.",
    keyPoints: [
      "Message automatique J-3 (rappel arrivée + infos pratiques)",
      "Message J+1 (satisfaction immédiate)",
      "Demande d'avis J+2 (moment optimal)",
      "Alertes automatiques sur incidents récurrents",
    ],
    terrain: {
      biens: 14,
      result:
        "Sur 14 logements automatisés, le taux d'avis 5★ est passé de 71% à 89% en 4 mois, simplement en optimisant le timing des messages.",
    },
  },
  {
    number: "04",
    page: "Page 07",
    title: "Pilotage qualité",
    subtitle: "Ce qui ne se mesure pas ne s\'améliore pas",
    color: "#4A6741",
    colorLight: "rgba(74,103,65,0.08)",
    colorBorder: "rgba(74,103,65,0.2)",
    description:
      "Le pilotage par KPIs transforme votre gestion réactive en management proactif. Vous anticipez les problèmes avant qu'ils deviennent des avis négatifs.",
    keyPoints: [
      "% d'avis 5★ par logement (cible : >85%)",
      "Nombre d\'incidents par période",
      "Temps moyen de réponse aux voyageurs",
      "Taux de conformité des check-lists",
    ],
    terrain: {
      biens: 22,
      result:
        "Conciergerie 22 biens : le suivi mensuel des KPIs a permis d'identifier 3 logements sous-performants et de corriger leurs problèmes avant la saison haute.",
    },
  },
];

export default function PiliersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activePilier, setActivePilier] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const p = piliers?.[activePilier];

  return (
    <section
      id="piliers"
      ref={sectionRef}
      className="py-28 md:py-40 px-6 md:px-16"
      style={{ background: "var(--cream)" }}
    >
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div
          className={`flex items-center gap-6 mb-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <span className="font-serif italic text-sm" style={{ color: "var(--sage)" }}>
            Pages 04 — 07
          </span>
          <div className="gold-line" />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--warm-gray)" }}>
            Les 4 Piliers
          </span>
        </div>

        <h2
          className={`font-serif italic leading-tight mb-16 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", color: "var(--charcoal)" }}
        >
          La structure qui transforme{" "}
          <span className="not-italic shimmer-text">votre conciergerie.</span>
        </h2>

        {/* Pilier tabs */}
        <div
          className={`flex flex-wrap gap-3 mb-16 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {piliers?.map((pilier, i) => (
            <button
              key={i}
              onClick={() => setActivePilier(i)}
              className="px-5 py-3 rounded-full text-sm font-bold transition-all duration-300"
              style={{
                background: activePilier === i ? pilier?.color : "white",
                color: activePilier === i ? "white" : "var(--warm-gray)",
                border: `1.5px solid ${activePilier === i ? pilier?.color : "var(--border)"}`,
                boxShadow: activePilier === i ? `0 4px 20px ${pilier?.color}40` : "none",
              }}
            >
              {pilier?.number} {pilier?.title}
            </button>
          ))}
        </div>

        {/* Active pilier content */}
        <div
          key={activePilier}
          className={`grid grid-cols-1 lg:grid-cols-12 gap-12 transition-all duration-500 ${visible ? "opacity-100" : "opacity-0"}`}
          style={{ animation: "fadeSlideIn 0.4s ease forwards" }}
        >
          {/* Left */}
          <div className="lg:col-span-5">
            <div
              className="rounded-2xl p-10 h-full"
              style={{
                background: p?.colorLight,
                border: `1px solid ${p?.colorBorder}`,
              }}
            >
              <div
                className="font-serif italic text-7xl mb-6 leading-none"
                style={{ color: p?.color, opacity: 0.3 }}
              >
                {p?.number}
              </div>
              <h3
                className="font-serif italic text-3xl mb-2"
                style={{ color: "var(--charcoal)" }}
              >
                {p?.title}
              </h3>
              <p className="text-sm font-bold mb-6" style={{ color: p?.color }}>
                {p?.subtitle}
              </p>
              <p className="text-sm leading-relaxed mb-8" style={{ color: "var(--warm-gray)" }}>
                {p?.description}
              </p>

              {/* Key points */}
              <ul className="space-y-3">
                {p?.keyPoints?.map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm" style={{ color: "var(--charcoal)" }}>
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                      style={{ background: p?.color }}
                    />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Terrain example */}
          <div className="lg:col-span-7">
            <div
              className="rounded-2xl p-10 mb-8"
              style={{
                background: "var(--ivory)",
                border: "1px solid var(--border)",
              }}
            >
              <div className="flex items-center gap-4 mb-6">
                <span
                  className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full"
                  style={{ background: p?.color, color: "white" }}
                >
                  Exemple terrain
                </span>
                <span
                  className="font-serif italic text-2xl"
                  style={{ color: "var(--charcoal)" }}
                >
                  {p?.terrain?.biens} logements
                </span>
              </div>
              <p className="text-base leading-relaxed" style={{ color: "var(--warm-gray)" }}>
                {p?.terrain?.result}
              </p>
            </div>

            {/* Visual: Pilier navigation cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {piliers?.map((pilier, i) => (
                <button
                  key={i}
                  onClick={() => setActivePilier(i)}
                  className="pillar-card rounded-xl p-5 text-left"
                  style={{
                    borderColor: activePilier === i ? pilier?.color : undefined,
                    background: activePilier === i ? pilier?.colorLight : "white",
                  }}
                >
                  <div
                    className="font-serif italic text-2xl mb-2"
                    style={{ color: pilier?.color }}
                  >
                    {pilier?.number}
                  </div>
                  <div className="text-xs font-bold" style={{ color: "var(--charcoal)" }}>
                    {pilier?.title}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--warm-gray)" }}>
                    {pilier?.terrain?.biens} biens
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}